import { observer } from 'mobx-react-lite';
import React from 'react';
import ReactMapGl, { MapProvider, Marker, Popup } from 'react-map-gl';

import { useStores } from '@src/Common/Hooks/useStores';
import { NewMythicIcon } from '@src/Pages/WorldMap/MapIcon/MapIcon';

import AddEventModal from '../Modal/AddEventModal/AddEventModal';
import DefaultModal from '../Modal/DefaultModal/DefaultModal';
import SignInModal from '../SignInModal/SignInModal';
import SubmitEventModal from '../SubmitEventModal/SubmitEventModal';

import CustomLoading from '@src/Components/Atoms/CustomLoading/CustomLoading';
import { logEvent } from '@src/Domain/Analytic';
import { AnalyticsEvent } from '@src/Domain/Analytic/Analytic.type';
import DetailEventPopup from '../DetailEventPopup/DetailEventPopup';
import PopupCustomMultiple from '../PopupCustomMultiple/PopupCustomMultiple';
import styles from './Maps.module.scss';
import useMaps from './useMaps';
const Maps = observer(function Maps() {
  const { FormStore, UserStore, DUMEventStore } = useStores();
  const { show, isChangeLocation, latitude, longitude } = FormStore;

  const {
    showSignInModal,
    setShowSignInModal,
    submitEventModal,
    setSubmitEventModal,
    showModalSubmitEventMobile,
    setShowModalSubmitEventMobile,
    mapRef,
    onLoad,
    showPopup,
    setShowPopup,
    hoverData,
    setHoverData,
    eventDetail,
    setEventDetail,
    viewState,
    setViewState,
    popupData,
    setPopupData,
    isAuthenticated,
    signinRedirect,
    normalizeCoordinates,
    handleValidationClick,
    onSubmitEventMobile
  } = useMaps();

  return (
    <>
      <article className={styles['InnerContainer']}>
        <MapProvider>
          <ReactMapGl
            projection={{ name: 'mercator' }}
            {...viewState}
            onMove={(evt) => setViewState(evt.viewState)}
            onDrag={() => UserStore.updateDetectedAction(true)}
            ref={mapRef}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_KEY}
            mapLib={import('mapbox-gl')}
            style={{ height: '100vh', width: '100vw', overflow: 'hidden' }}
            mapStyle="mapbox://styles/sahatilfat/cljgki05600cj01pa9m6mc4bw"
            onLoad={onLoad}
            minZoom={2}
            maxZoom={12}
            dragRotate={false}
            bearing={0}
            onClick={(e) => {
              e.preventDefault();
              const [lat, long] = normalizeCoordinates(
                e.lngLat.lat,
                e.lngLat.lng
              );
              setShowPopup(false);
              FormStore.clearCoordinate();
              handleValidationClick(767, isChangeLocation, lat, long);
            }}
          >
            {!DUMEventStore.mapReady ? (
              <section className={styles['ContainerLoading']}>
                <div className={styles['default-loading']}>
                  <div className={styles['loading']}>
                    <CustomLoading />
                  </div>
                </div>
              </section>
            ) : null}
            {DUMEventStore.DUMEventDatas.dataMaps.map((item, key) => {
              return (
                <Marker
                  key={Number(key)}
                  latitude={item.coordinates[0]}
                  longitude={item.coordinates[1]}
                >
                  <div
                    id="marker-event"
                    className={`${styles['pulse']} ${
                      item.isMythicEvent ? styles['mythic'] : ''
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      logEvent(
                        AnalyticsEvent.BUTTON_CLICKED,
                        'DUM Map Event Detail Button Click'
                      );
                      DUMEventStore.selectedDetailDumGlobalIdIndex = 0;
                      setEventDetail({
                        city: item.city,
                        coordinates: [item.coordinates[0], item.coordinates[1]],
                        id: item.id,
                        hoverData: item.hoverData,
                        isMythicEvent: item.isMythicEvent
                      });
                    }}
                    onMouseEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPopupData((prev) => ({
                        ...prev,
                        latitude: item.coordinates[0],
                        longitude: item.coordinates[1],
                        message: item.city
                      }));
                      setHoverData(item.hoverData);
                      setShowPopup(true);
                    }}
                    onMouseLeave={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {item.isMythicEvent && <NewMythicIcon />}
                  </div>
                </Marker>
              );
            })}

            {showPopup && (
              <Popup
                latitude={popupData.latitude}
                longitude={popupData.longitude}
              >
                <PopupCustomMultiple
                  locationDetail={popupData.message}
                  coordinate={[popupData.latitude, popupData.longitude]}
                  dumId={popupData.dumId}
                  data={hoverData}
                  onClickItem={(globalEventId) => {
                    logEvent(
                      AnalyticsEvent.BUTTON_CLICKED,
                      'DUM Map Event Detail Button Click'
                    );
                    DUMEventStore.selectedDetailDumGlobalIdIndex =
                      globalEventId;
                    setEventDetail({
                      city: popupData.message,
                      coordinates: [popupData.latitude, popupData.longitude],
                      id: popupData.dumId,
                      hoverData: hoverData,
                      isMythicEvent: popupData.isMythicEvent
                    });
                  }}
                />
              </Popup>
            )}

            {submitEventModal.show && (
              <>
                <Popup
                  latitude={submitEventModal.coordinate[0]}
                  longitude={submitEventModal.coordinate[1]}
                >
                  <SubmitEventModal
                    onSubmitEvent={onSubmitEventMobile}
                    isAuthenticated={isAuthenticated}
                    locationName={submitEventModal.location}
                    lat={submitEventModal.coordinate[0]}
                    lon={submitEventModal.coordinate[1]}
                  />
                </Popup>
                <Marker
                  latitude={submitEventModal.coordinate[0]}
                  longitude={submitEventModal.coordinate[1]}
                  // offset={[-1, 8]}
                >
                  <div className={styles['submit-marker']}></div>
                </Marker>
              </>
            )}
          </ReactMapGl>
        </MapProvider>
      </article>

      {eventDetail &&
      DUMEventStore.selectedDetailDumGlobalIdIndex !== undefined ? (
        <DetailEventPopup
          onClose={() => {
            DUMEventStore.selectedDetailDumGlobalIdIndex = undefined;
            setEventDetail(undefined);
          }}
          eventData={eventDetail}
        />
      ) : null}

      {show ? (
        <AddEventModal
          show={show}
          onClose={() => {
            FormStore.hideModal();
            FormStore.setNonActiveChangeLocation();
            FormStore.clearCoordinate();
            setSubmitEventModal((prev) => ({
              ...prev,
              show: false,
              coordinate: [0, 0]
            }));
          }}
          data={{
            name: '',
            date: '',
            link: '',
            description: '',
            latitude: latitude,
            longitude: longitude
          }}
          onChangeLocation={() => {
            FormStore.hideModal();
            FormStore.setActiveChangeLocation();
            setSubmitEventModal((prev) => ({
              ...prev,
              show: false
            }));
          }}
          locationName={submitEventModal.location}
          closeDetailEvent={() => {
            //
          }}
        />
      ) : null}
      <SignInModal
        show={showSignInModal}
        onButtonClick={async () => {
          await signinRedirect();
          localStorage.setItem('sign', window.location.pathname);
        }}
        onClose={() => setShowSignInModal(!showSignInModal)}
      />
      <DefaultModal
        show={showModalSubmitEventMobile}
        onClose={() => setShowModalSubmitEventMobile(false)}
      >
        <SubmitEventModal
          onSubmitEvent={onSubmitEventMobile}
          locationName={submitEventModal.location}
          lat={submitEventModal.coordinate[0]}
          lon={submitEventModal.coordinate[1]}
          isAuthenticated={isAuthenticated}
        />
      </DefaultModal>
    </>
  );
});

export default Maps;
